/**
 * NEXUMA — usePermissions Hook
 *
 * React hook that provides permission-checking utilities for components.
 * Reads from the current authenticated user and exposes helpers.
 *
 * Usage:
 *   const { hasPermission, hasAnyPermission, can, cannot, primaryRole } = usePermissions();
 *   if (can('employees.view.company')) { ... }
 */

import { useMemo, useEffect, useState } from 'react';
import globals from '../services/globals';
import permissionService, { refreshEffectivePermissions } from '../services/permissionService';

export default function usePermissions() {
  const user = globals().getLogedInUser();

  // Load the backend's real effective permissions once (deduped across hooks),
  // then bump a version so this component re-gates on the loaded set. Until it
  // resolves, permissionService falls back to the static role resolution.
  const [permsVersion, setPermsVersion] = useState(0);
  useEffect(() => {
    let alive = true;
    refreshEffectivePermissions().then(() => { if (alive) setPermsVersion((v) => v + 1); });
    return () => { alive = false; };
  }, [user?.data?.id]);

  const permissions = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => permissionService.getPermissions(user),
    // permsVersion bumps once the real effective set loads → recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.data?.id, user?.data?.roles?.length, permsVersion]
  );

  const roleKeys = useMemo(
    () => permissionService.getRoleKeys(user),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.data?.id]
  );

  const primaryRole = useMemo(
    () => permissionService.getPrimaryRole(user),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.data?.id]
  );

  return {
    /** All resolved permission strings for this user */
    permissions,

    /** Resolved canonical role keys (e.g. ['company_admin']) */
    roleKeys,

    /** Highest-priority role config object */
    primaryRole,

    /** Returns true if user has the given permission (scope-aware) */
    hasPermission: (key) => permissionService.hasPermission(key, user),

    /** Returns true if user has AT LEAST ONE of the given permissions */
    hasAnyPermission: (keys) => permissionService.hasAnyPermission(keys, user),

    /** Returns true if user has ALL of the given permissions */
    hasAllPermissions: (keys) => permissionService.hasAllPermissions(keys, user),

    /** Alias for hasPermission — more readable in JSX */
    can: (key) => permissionService.hasPermission(key, user),

    /** Inverse of can */
    cannot: (key) => !permissionService.hasPermission(key, user),

    /** Check if user has a specific role */
    hasRole: (roleKey) => permissionService.hasRole(roleKey, user),

    /** Check if user has any of the given roles */
    hasAnyRole: (roleKeys) => permissionService.hasAnyRole(roleKeys, user),

    /** Get the highest scope for a module+action */
    getMaxScope: (module, action) => permissionService.getMaxScope(module, action, user),

    /** Is this user "worker-only" (non-admin) */
    isWorkerOnly: permissionService.isWorkerOnly(user),

    /** Filter sidebar config to visible items */
    getVisibleMenuItems: (config) => permissionService.getVisibleMenuItems(config, user),

    /** Filter dashboard widgets to visible items */
    getVisibleWidgets: (config) => permissionService.getVisibleWidgets(config, user),

    /** The raw user object */
    user,
  };
}
